import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchProjects } from '../../../fetchProjects';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const FeaturedProjects = () => {
  const [hoveredProject, setHoveredProject] = useState(null);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const projects = await fetchProjects();
        const projectsArray = Array.isArray(projects) ? projects : [];
        // Get featured projects or fallback to first 2
        const featured = projectsArray.filter(p => p.featured).slice(0, 4);
        setFeaturedProjects(featured.length > 0 ? featured : projectsArray.slice(0, 2));
      } catch (err) {
        console.error('Error fetching featured projects:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  return (
    <section className="py-16 lg:py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-text-primary mb-4">
            Featured Work
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            A curated selection of projects that demonstrate my approach to solving complex problems 
            through clear design and strategic thinking.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-10 mb-12">
          {loading ? (
            [1, 2].map((i) => (
              <div key={`skeleton-${i}`} className="animate-pulse bg-white/5 rounded-2xl h-[450px] lg:h-[500px]"></div>
            ))
          ) : (
            featuredProjects.map((project) => (
            <div
              key={project.id}
              className="group relative bg-white/5 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10 shadow-2xl hover:shadow-accent/10 transition-smooth transform hover:-translate-y-2"
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              {/* Project Image */}
              <div className="relative h-64 lg:h-72 overflow-hidden">
                <Image
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-smooth"
                />
                
                {/* Overlay with Metrics */}
                <div className={`absolute inset-0 bg-primary/95 flex items-center justify-center transition-smooth px-4 ${
                  hoveredProject === project.id ? 'opacity-100' : 'opacity-0'
                }`}>
                  <div className="text-center text-white w-full">
                    <div className="grid grid-cols-2 gap-y-4 gap-x-2 mb-6">
                      {project.metrics && Object.entries(project.metrics).slice(0, 4).map(([key, value]) => (
                        <div key={key} className="text-center px-1">
                          <div className="font-heading font-bold text-base text-accent">{value}</div>
                          <div className="text-[10px] opacity-70 uppercase tracking-tighter leading-tight">{key.replace(/_/g, ' ')}</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-col gap-3 items-center">
                      <Link
                        to={`/projects/${project.id}`}
                        className="w-full max-w-[160px]"
                        title={`View Deep Dive Case Study: ${project.title}`}
                        aria-label={`Navigate to the detailed case study for ${project.title}`}
                      >
                        <Button variant="secondary" size="sm" className="w-full" iconName="ArrowRight" iconPosition="right">
                          View Case Study
                        </Button>
                      </Link>
                      {project.link && project.link.startsWith('http') && (
                        <a 
                          href={project.link} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          title={`View the live application or demo for ${project.title}`}
                          className="text-xs text-text-muted hover:text-white transition-smooth underline"
                        >
                          Live Demo
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="bg-background/90 backdrop-blur-md text-accent text-[10px] font-bold px-3 py-1.5 rounded-full border border-accent/30 uppercase tracking-widest shadow-xl">
                    {project.type}
                  </span>
                </div>
              </div>

              {/* Project Content */}
              <div className="p-6 lg:p-8">
                <h3 className="font-heading font-semibold text-xl lg:text-2xl text-text-primary mb-3 group-hover:text-accent transition-smooth">
                  {project.title}
                </h3>
                <p className="text-text-secondary mb-4 leading-relaxed">
                  {project.description}
                </p>

                {/* Technologies */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {Array.isArray(project.technologies) && project.technologies.map((tech) => (
                    <span
                      key={tech}
                      className="text-xs bg-white/5 text-text-secondary px-2 py-1 rounded-md border border-white/5"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                {/* View Project Link */}
                <Link
                  to={`/projects/${project.id}`}
                  title={`Read detailed technical analysis of ${project.title}`}
                  className="inline-flex items-center gap-2 text-accent hover:text-accent-400 font-medium text-sm transition-smooth group/link"
                >
                  Deep Dive Analysis
                  <Icon name="ArrowRight" size={16} className="group-hover/link:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          ))
        )}
        </div>

        {/* View All Projects CTA */}
        <div className="text-center">
          <Button variant="outline" size="lg" iconName="Grid" iconPosition="left">
            <Link 
              to="/projects"
              title="Browse Sudhan R's Full Portfolio of Projects"
              aria-label="Navigate to the Complete Portfolio"
            >
              View All Projects
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProjects;